import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { generatePath, Link, useLocation } from 'react-router'

import { BusinessRoute } from '@/constants/paths'
import { Pause, Play, Plus, Trash, UserCross } from '@/shared/icons/outline'
import { Ellipsis } from '@/shared/icons/solid'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui'

import {
  ContractorDetails,
  ContractorsTransaction,
  ContractorTransactionState,
  ContractState,
} from '../../types'

import { DeleteContractorDialog } from './DeleteContractorDialog'
import { PauseContractDialog } from './PauseContractDialog'
import { PendingPaymentDialog } from './PendingPaymentDialog'
import { UnPauseContractDialog } from './UnpauseContractDialog'

type DialogType = 'delete' | 'pause' | 'unpause' | 'none' | 'pending-payments'

type Props = {
  contractor?: ContractorDetails
  payments?: ContractorsTransaction[]
}

export const ContractorActions = ({ contractor, payments }: Props) => {
  const intl = useIntl()
  const location = useLocation()

  const [dialogType, setDialogType] = useState<DialogType>('none')

  const latestContract = contractor?.contracts[0]

  const hasPendingPayments = payments?.some(
    (payment) => payment.state === ContractorTransactionState.PENDING,
  )

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={intl.formatMessage({
              id: 'action.showMoreActions',
              defaultMessage: 'Show more actions',
            })}
            size="inline"
            className="size-10"
            variant="tertiary"
          >
            <Ellipsis />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {(() => {
            switch (latestContract?.state) {
              case ContractState.TERMINATED:
                return (
                  <>
                    <DropdownMenuItem>
                      <Button
                        leftIcon={<Plus className="size-5" />}
                        size="sm"
                        variant="dropdown"
                        width="full"
                        asChild
                      >
                        <Link
                          to={generatePath(
                            BusinessRoute.ContractorsAddContract,
                            {
                              id: contractor?.id ?? '',
                            },
                          )}
                          state={{ from: location }}
                        >
                          <FormattedMessage
                            id="label.newContract"
                            defaultMessage="New contract"
                          />
                        </Link>
                      </Button>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Button
                        leftIcon={
                          <Trash className="text-primary-error size-4" />
                        }
                        size="sm"
                        variant="dropdown"
                        className="text-primary-error"
                        width="full"
                        onClick={() =>
                          setDialogType(
                            hasPendingPayments ? 'pending-payments' : 'delete',
                          )
                        }
                      >
                        <FormattedMessage
                          id="label.deleteContractor"
                          defaultMessage="Delete contractor"
                        />
                      </Button>
                    </DropdownMenuItem>
                  </>
                )

              case ContractState.TERMINATION_PENDING:
                return (
                  <DropdownMenuItem>
                    <Button
                      leftIcon={<Plus className="size-5" />}
                      size="sm"
                      variant="dropdown"
                      width="full"
                      asChild
                    >
                      <Link
                        to={generatePath(BusinessRoute.ContractorsAddContract, {
                          id: contractor?.id ?? '',
                        })}
                        state={{ from: location }}
                      >
                        <FormattedMessage
                          id="label.newContract"
                          defaultMessage="New contract"
                        />
                      </Link>
                    </Button>
                  </DropdownMenuItem>
                )

              case ContractState.PAUSED:
                return (
                  <DropdownMenuItem>
                    <Button
                      leftIcon={<Play className="size-4" />}
                      size="sm"
                      variant="dropdown"
                      width="full"
                      onClick={() => setDialogType('unpause')}
                    >
                      <FormattedMessage
                        id="label.resumeContract"
                        defaultMessage="Resume contract"
                      />
                    </Button>
                  </DropdownMenuItem>
                )

              case ContractState.ACTIVE:
                return (
                  <>
                    <DropdownMenuItem>
                      <Button
                        leftIcon={<Pause className="size-3" />}
                        size="sm"
                        variant="dropdown"
                        width="full"
                        onClick={() => setDialogType('pause')}
                      >
                        <FormattedMessage
                          id="label.pauseContract"
                          defaultMessage="Pause contract"
                        />
                      </Button>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Button
                        leftIcon={<UserCross className="size-4" />}
                        size="sm"
                        variant="dropdown"
                        width="full"
                        asChild
                      >
                        <Link
                          to={generatePath(
                            BusinessRoute.ContractorsEndContract,
                            {
                              id: contractor?.id ?? '',
                            },
                          )}
                        >
                          <FormattedMessage
                            id="label.endContract"
                            defaultMessage="End contract"
                          />
                        </Link>
                      </Button>
                    </DropdownMenuItem>
                  </>
                )

              default:
                return null
            }
          })()}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteContractorDialog
        contractor={contractor}
        isOpen={dialogType === 'delete'}
        onOpenChange={() => {
          setDialogType('none')
        }}
      />

      <PendingPaymentDialog
        isOpen={dialogType === 'pending-payments'}
        onOpenChange={() => {
          setDialogType('none')
        }}
      />

      <PauseContractDialog
        contractor={contractor}
        isOpen={dialogType === 'pause'}
        onOpenChange={() => {
          setDialogType('none')
        }}
      />

      <UnPauseContractDialog
        contractor={contractor}
        isOpen={dialogType === 'unpause'}
        onOpenChange={() => {
          setDialogType('none')
        }}
      />
    </>
  )
}
